import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useUser } from './UserContext';
import { createBiqConversationId, prewarmBiqSession } from '../utils/api';
import {
  deriveChatTitle,
  fetchConversations,
  fetchConversationMessages,
  LIST_OK,
} from '../utils/biqChatHistoryStorage';
import { useBiqLiveChats } from './BiqLiveChatsContext';

const BiqChatHistoryContext = createContext(null);

/** Load in flight. */
export const HISTORY_LOADING = 'loading';
/** The server answered: `chats` is authoritative, empty or not. */
export const HISTORY_READY = 'ready';
/** The load failed. `chats` may be stale or empty - it is NOT a confirmed empty list. */
export const HISTORY_ERROR = 'error';

/**
 * Server conversation summary -> the shape SidebarChatHistory renders.
 * title_source stays null intentionally when auto-titling times out (see
 * title_generator.py) so a later turn can retry - c.title can genuinely be
 * missing for an otherwise-real conversation. Fall back to the session's
 * last_message_preview (always set once a user message has been stored) so
 * the sidebar row is never a blank button.
 */
const toSidebarChat = (c) => ({
  id: c.conversation_id,
  title: c.title || c.last_message_preview || 'New conversation',
  titleSource: c.title_source || null,
  createdAt: c.created_at,
  updatedAt: c.updated_at,
  // Guardrail state (persistent-sessions plan, Phase 3 section A) - written
  // onto the session doc by container_chat_app's /chat route and read back
  // here IF the conversation_sessions list payload includes it (not every
  // deployed version does yet). BiQ.jsx uses this to show the right banner
  // immediately on reload, before any new turn's SSE `context_status` event
  // arrives. Absent field -> null, same as titleSource above.
  contextStatus: c.context_status || null,
  contextTokens: c.context_tokens ?? null,
  contextCeiling: c.context_ceiling ?? null,
});

/**
 * Server message -> the shape BiQ.jsx's message list renders.
 * ponytail: a restored-from-server message only carries {id, type, content,
 * timestamp, chatId} — chart data, PDF payloads, and per-message feedback
 * state live only in the live-stream session, not in Firestore, so a
 * restored message renders as plain text/markdown without those extras.
 * Upgrade path: extend GET /conversations/<id>/messages to persist and
 * return those fields once a full mapper is actually needed.
 */
const toUiMessage = (m) => ({
  id: m.message_id,
  type: m.role === 'user' ? 'user' : 'biq',
  content: m.content || '',
  timestamp: m.timestamp,
  chatId: m.chat_id || null,
  isStreaming: false,
  // message_id is the same Firestore id POST /chat expects back as
  // edit_of_message_id / regenerate_of_message_id, so edit + regenerate
  // keep working on a restored (page-reload) message.
  // ponytail: this id is only ever populated for a RESTORED message (this
  // mapper). A message from the current live stream never gets one - the
  // backend's `done` SSE event doesn't return store_message()'s doc ids
  // (pre-existing gap, not introduced here) - so edit/regenerate on a
  // just-sent message (before any reload) has no firestoreMessageId to send
  // and silently no-ops via the `message.firestoreMessageId ? {...} : {}`
  // guard at the call sites. Upgrade path: return user_message_id/
  // assistant_message_id on `done` in app.py.
  firestoreMessageId: m.message_id,
});

export const BiqChatHistoryProvider = ({ children }) => {
  const { email } = useUser();
  const live = useBiqLiveChats();
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(() => createBiqConversationId());
  const [historyStatus, setHistoryStatus] = useState(HISTORY_LOADING);
  // Bumped by reloadChats() to re-run the loader without a page reload.
  const [reloadToken, setReloadToken] = useState(0);

  const chatsRef = useRef(chats);
  chatsRef.current = chats;

  const activeChatIdRef = useRef(activeChatId);
  activeChatIdRef.current = activeChatId;

  const loadedEmailRef = useRef(undefined);

  useEffect(() => {
    const previousEmail = loadedEmailRef.current;
    loadedEmailRef.current = email;

    const isAccountSwitch =
      previousEmail !== undefined && previousEmail !== null && previousEmail !== email;
    if (isAccountSwitch) {
      live.clear();
      setActiveChatId(createBiqConversationId());
      // Clear synchronously, not just on the eventual fetch response - the
      // merge below preserves any "local-only" id (one absent from the
      // fetch), which on an account switch is every previous account's
      // chat. Without this, the prior user's titles stay visible in the
      // sidebar on a shared device until the new fetch happens to complete.
      setChats([]);
    }

    if (!email) {
      setChats([]);
      // Deliberately NOT ready: a signed-out/not-yet-resolved user has an
      // unknown history, not a confirmed empty one. Claiming "ready" here
      // would flash "No chats yet" during the auth blip that this whole
      // retry path exists to survive.
      setHistoryStatus(HISTORY_LOADING);
      return;
    }

    let cancelled = false;

    const load = async () => {
      const { status, conversations } = await fetchConversations();
      // Dropping it is correct - the
      // effect re-run that cancelled us is doing its own load.
      if (cancelled) return;

      if (status !== LIST_OK) {
        setHistoryStatus(HISTORY_ERROR);
        return;
      }

      const fetched = conversations.map(toSidebarChat);
      // Merge, don't replace: a chat can be created/updated locally (an
      // optimistic placeholder, an already-applied server title) while this
      // initial fetch is still in flight — a blind overwrite would erase it.
      setChats((prev) => {
        const prevById = new Map(prev.map((c) => [c.id, c]));
        const fetchedIds = new Set(fetched.map((c) => c.id));
        const localOnly = prev.filter((c) => !fetchedIds.has(c.id));
        // Same-ID race: an SSE-applied server title (or a PATCH rename) can
        // land locally after this fetch snapshotted Firestore but before
        // this callback commits - in that case `prev`'s copy is the newer
        // one. Keep it: prefer local when it has a titleSource the fetched
        // copy doesn't (fetched hasn't caught up yet).
        const reconciled = fetched.map((f) => {
          const local = prevById.get(f.id);
          if (local?.titleSource && !f.titleSource) return { ...f, ...local };
          return f;
        });
        return [...localOnly, ...reconciled];
      });
      setHistoryStatus(HISTORY_READY);
    };

    setHistoryStatus(HISTORY_LOADING);
    load();

    return () => {
      cancelled = true;
    };
  }, [email, reloadToken, live]);

  /** Re-run the sidebar load (manual "Retry" affordance after HISTORY_ERROR). */
  const reloadChats = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const startNewChat = useCallback(() => {
    const id = createBiqConversationId();
    setActiveChatId(id);
    return id;
  }, []);

  const selectChat = useCallback((id) => {
    if (!id) return;
    // Resume flow: the sidebar only calls selectChat with an id already in
    // `chats` (a real, previously-sent session) - a brand-new/never-sent
    // thread is switched to via startNewChat instead, never this path. Fire
    // the cache-warm request for exactly that resume case, before switching
    // (best-effort/fire-and-forget - prewarmBiqSession never throws and the
    // UI never awaits it; see plan Phase 3 section C / Phase 4b).
    if (id !== activeChatIdRef.current && chatsRef.current.some((c) => c.id === id)) {
      prewarmBiqSession(id);
    }
    setActiveChatId((current) => (current === id ? current : id));
  }, []);

  /**
   * Fetch a thread's messages from the backend, mapped for direct render.
   * A chat id that hasn't appeared in `chats` yet (a brand-new, never-sent
   * thread) has no session doc on the backend — skip the round trip that
   * would just 404 into an empty list.
   */
  const getChatMessages = useCallback(async (id) => {
    if (!id) return [];
    if (!chatsRef.current.some((c) => c.id === id)) return [];
    const messages = await fetchConversationMessages(id);
    const mapped = messages.map(toUiMessage);
    // Backfill each assistant bubble's `userPrompt` from the preceding user
    // message so Regenerate (which resends message.userPrompt) still works
    // after a restore, without the backend needing to store it redundantly.
    for (let i = 1; i < mapped.length; i++) {
      if (mapped[i].type === 'biq' && mapped[i - 1].type === 'user') {
        mapped[i].userPrompt = mapped[i - 1].content;
      }
    }
    return mapped;
  }, []);

  /**
   * Immediate local-only placeholder title for a new/active thread, shown
   * before the backend's real title exists. Never written to the backend.
   * A server title (auto or user, via applyServerTitle) always wins and is
   * never reverted back to a placeholder.
   */
  const applyLocalTitlePlaceholder = useCallback((id, messages) => {
    if (!id) return;
    const title = deriveChatTitle(messages);
    if (!title) return;
    setChats((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      const now = new Date().toISOString();
      if (idx === -1) {
        return [{ id, title, titleSource: null, createdAt: now, updatedAt: now }, ...prev];
      }
      const existing = prev[idx];
      // A server title already won here — still bump activity/position
      // (this chat is the one being talked in right now), just keep its title.
      const nextTitle = existing.titleSource ? existing.title : title;
      const updated = { ...existing, title: nextTitle, updatedAt: now };
      // Move to front, matching "most recently active" sidebar ordering.
      return [updated, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });
  }, []);

  /** Server-confirmed title (from PATCH response or the `conversation_title` SSE event). Always wins. */
  const applyServerTitle = useCallback((id, title, titleSource) => {
    if (!id || !title) return;
    setChats((prev) => {
      const idx = prev.findIndex((c) => c.id === id);
      const now = new Date().toISOString();
      const updated = { title, titleSource: titleSource || 'auto', updatedAt: now };
      if (idx === -1) {
        return [{ id, createdAt: now, ...updated }, ...prev];
      }
      return [{ ...prev[idx], ...updated }, ...prev.slice(0, idx), ...prev.slice(idx + 1)];
    });
  }, []);

  const replaceChatId = useCallback((previousId, nextId) => {
    if (!previousId || !nextId || previousId === nextId) return;
    live.rekey(previousId, nextId);
    const previous = chatsRef.current;
    if (previous.some((c) => c.id === previousId)) {
      setChats(previous.map((c) => (c.id === previousId ? { ...c, id: nextId } : c)));
    }
    setActiveChatId((current) => (current === previousId ? nextId : current));
  }, [live]);

  const value = useMemo(
    () => ({
      chats,
      historyStatus,
      reloadChats,
      activeChatId,
      startNewChat,
      selectChat,
      getChatMessages,
      applyLocalTitlePlaceholder,
      applyServerTitle,
      replaceChatId,
    }),
    [
      chats,
      historyStatus,
      reloadChats,
      activeChatId,
      startNewChat,
      selectChat,
      getChatMessages,
      applyLocalTitlePlaceholder,
      applyServerTitle,
      replaceChatId,
    ]
  );

  return (
    <BiqChatHistoryContext.Provider value={value}>
      {children}
    </BiqChatHistoryContext.Provider>
  );
};

export const useBiqChatHistory = () => {
  const context = useContext(BiqChatHistoryContext);
  if (!context) {
    throw new Error('useBiqChatHistory must be used within a BiqChatHistoryProvider');
  }
  return context;
};
